import React, {
  createContext,
  ReactNode,
  useContext,
  useState,
} from "react";
import { useMutation } from '@tanstack/react-query'
import { apiModulo } from '../services/api'
import { useModal } from "../hooks/ModalContext";
import { Divida } from '../types/divida-interface'
import { Contrato } from "../types/contratos-data-interface";
import { ConsultaParametrosData, ConsultaParametrosArray, SimularAcordoData } from "../types/simular-acordo-interface";
import { AxiosError } from "axios";
import { ConsultaAcordoRequest, ConsultaAcordoResponse, Parcelas } from '../types/consulta-acordo-interface'

interface NegContextProviderProps {
  children: ReactNode;
}

interface ContratoRequest {
  numcontrato: string;
  filial: string;
  numeroparcela: string;
  idparcial: string;
  valpago: string;
  datvenci: string;
}

export interface RequestParamsSimulate {
  url?: string;
  cpf_cnpj: string;
  codcredor?: string;
  contrato?: string;
  formapagamento?: string;
  contratos?: ContratoRequest[]
  tipo: string
  app?: string
  usuario?: string
}

export interface ValoresSimulacao {
  juros: string;
  multa: string;
  valorOriginal: string;
  valorCorrigido?: string;
  desconto?: string
  total?: string
}

interface Parcelamentos {
  tipo: string;
  qtdeparcela: string;
  valorentrada: string;
  vencimento: string;
  formapagamento: string;
  percentual_desconto: string;
  percentual_isencaojuros: string;
  percentual_isencaomulta: string;
  percentual_juros_renegociacao: string;
}

export interface RequestSimularParametros {
  url: string;
  cpf_cnpj: string;
  codcredor: string;
  usuario: string;
  parcelamentos: Parcelamentos[]
  contratos: ContratoRequest[]
  app?: string
}

export interface DescontosInputProps {
  percentual_desconto: string;
  percentual_isencaojuros: string;
  percentual_isencaomulta: string;
  percentual_juros_renegociacao: string;
}


interface NegContextProps {
  parcelasNeg: Divida[]
  stateSelection: string
  contratoNeg: Contrato
  simulacaoAP: ConsultaParametrosData
  simulacaoAV: ConsultaParametrosData
  valoresSimulacao: ValoresSimulacao
  simulacaoAPJP: ConsultaParametrosArray
  simulcaoCalculadora: SimularAcordoData
  descontos: DescontosInputProps
  addParcelasNeg(parcela: Divida): void
  addParcelasNegGroup(parcelas: Divida[], unSelectAll?: boolean): void
  clearParcelasNeg(): void
  getValores(desconto?: string, isAP?: boolean, totalDivida?: string): void
  setSelection(state: string): void
  addContrato(contrato: Contrato, isOperador?: boolean): void
  getParamsSimulate(params: RequestParamsSimulate): Promise<void>
  simulacaoJPParcela(qtdparcela: string): void
  calcularValores(dataParams: RequestSimularParametros): Promise<void>
  getAcordos(data: ConsultaAcordoRequest): Promise<void>
  // setSelectedAcordo: React.Dispatch<React.SetStateAction<ConsultaAcordoResponse>>
  setDescontos: React.Dispatch<React.SetStateAction<DescontosInputProps>>
  setValorEntrada: React.Dispatch<React.SetStateAction<string>>
  setPercentualEntrada: React.Dispatch<React.SetStateAction<string>>
  setSimulacaoCalculadora: React.Dispatch<React.SetStateAction<SimularAcordoData>>
  setSelectedAcordoResponse: React.Dispatch<React.SetStateAction<ConsultaAcordoResponse>>
  setParcelaAcordo: React.Dispatch<React.SetStateAction<Parcelas>>
  valorEntrada: string
  percentualEntrada: string
  acordoResponse: ConsultaAcordoResponse
  selectedAcordoResponse: ConsultaAcordoResponse
  parcelaAcordo: Parcelas
}

const NegContext = createContext<NegContextProps>({} as NegContextProps);

export const NegProvider = ({ children }: NegContextProviderProps) => {
  const { setModal } = useModal()
  const [parcelasNeg, setParcelasNeg] = useState([] as Divida[])
  const [stateSelection, setStateSelection] = useState('unselect')
  const [contratoNeg, setContratoNeg] = useState({} as Contrato)
  const [valoresSimulacao, setValoresSimulacao] = useState({} as ValoresSimulacao)
  const [simulacaoAV, setSimulacaoAV] = useState<ConsultaParametrosData>({} as ConsultaParametrosData)
  const [simulacaoAP, setSimulacaoAP] = useState<ConsultaParametrosData>({} as ConsultaParametrosData)
  const [simulacaoAPJP, setSimulacaoAPJP] = useState<ConsultaParametrosArray>({} as ConsultaParametrosArray)
  const [simulcaoCalculadora, setSimulacaoCalculadora] = useState<SimularAcordoData>({} as SimularAcordoData)
  const [valorEntrada, setValorEntrada] = useState('0,00')
  const [percentualEntrada, setPercentualEntrada] = useState('0')
  const [acordoResponse, setAcordoResponse] = useState<ConsultaAcordoResponse>({} as ConsultaAcordoResponse)
  const [selectedAcordoResponse, setSelectedAcordoResponse] = useState<ConsultaAcordoResponse>({} as ConsultaAcordoResponse)
  const [parcelaAcordo, setParcelaAcordo] = useState<Parcelas>({} as Parcelas)
  const [descontos, setDescontos] = useState<DescontosInputProps>({
    percentual_desconto: '0',
    percentual_isencaojuros: '0',
    percentual_isencaomulta: '0',
    percentual_juros_renegociacao: '0'
  })

  const acordosMut = useMutation({
    mutationFn: ({ app, codcredor, contrato, cpf_cnpj, url, usuario }: ConsultaAcordoRequest) => apiModulo.post('/agreements', {
      app: app ? app : 'ACIONA_WEB', codcredor, contrato, cpf_cnpj, url, usuario
    }),
    onError: (error: AxiosError) => {
      const data = error?.response?.data as { message: string }
      setModal(data.message, 'Erro', false, true)
    },
    mutationKey: ['form']
  })

  const paramsSimulateMut = useMutation({
    mutationFn: ({ codcredor, contrato, cpf_cnpj, formapagamento, url, contratos, tipo, app, usuario }: RequestParamsSimulate) => apiModulo.post('/simulate/params', {
      codcredor, contrato, cpf_cnpj, formapagamento, url, contratos, tipo, app: app ? app : 'ACIONA_WEB', usuario
    }),
    onError: (error: AxiosError) => {
      const data = error?.response?.data as { message: string }
      if (data?.message.includes('ETIMEDOUT')) {
        setModal("Houve um problema de conexão. Por favor, tente novamente.", "Aviso", false, true);
        return
      }
      setModal(data.message, 'Erro', false, true)
    },
    mutationKey: ['form']
  })


  const simular = useMutation({
    mutationFn: ({ cpf_cnpj, codcredor, parcelamentos, url, contratos, usuario, app }: RequestSimularParametros) => apiModulo.post('/simulate', {
      url,
      cpf_cnpj, codcredor, parcelamentos, contratos, usuario, app: app ? app : 'ACIONA_WEB'
    }), mutationKey: ['form']
  })


  async function getParamsSimulate(params: RequestParamsSimulate) {
    const { data } = await paramsSimulateMut.mutateAsync(params)
    if (params.tipo === "AV") {
      setSimulacaoAV(data.message)
    } else if (params.tipo === 'AP' && params.codcredor === "2000") {
      setSimulacaoAPJP(data.message)
      setSimulacaoAP({
        centralatendimento: data.message?.centralatendimento,
        codcredor: data.message?.codcredor,
        contrato: data.message?.contrato,
        cpfcnpj: data.message?.cpfcnpj,
        nomcredor: data.message?.nomcredor,
        regrassimularacordo: data.message?.regrassimularacordo[0]
      })
    }
    else {
      setSimulacaoAP(data.message)
    }
  }

  function simulacaoJPParcela(qtdparcela: string) {
    const find = simulacaoAPJP?.regrassimularacordo?.find(item => item.quantidade_parcelas_max === qtdparcela)
    if (find) {
      setSimulacaoAP({
        centralatendimento: simulacaoAPJP?.centralatendimento,
        codcredor: simulacaoAPJP?.codcredor,
        contrato: simulacaoAPJP?.contrato,
        cpfcnpj: simulacaoAPJP?.cpfcnpj,
        nomcredor: simulacaoAPJP?.nomcredor,
        regrassimularacordo: find
      })
    }
  }

  async function calcularValores(dataParams: RequestSimularParametros) {
    const { data } = await simular.mutateAsync(dataParams)
    setSimulacaoCalculadora(data.message)
  }

  //Atualizar esse metodo para pegar 2 contratos (Quando for pedido)
  function addParcelasNeg(parcela: Divida) {
    const find = parcelasNeg.find(item => parcela.id === item.id)
    if (find) {
      const parcelasNovas = parcelasNeg.filter(item => item.id !== find.id)
      setParcelasNeg(parcelasNovas)
    } else {
      setParcelasNeg([...parcelasNeg, parcela])
    }
  }

  function converterParaNumeroAmericano(valorBrasileiro: string) {
    const semPontoMilhar = valorBrasileiro.replace(/\./g, '');
    const numeroAmericano = semPontoMilhar.replace(',', '.');
    return parseFloat(numeroAmericano);
  }

  function converterParaNumeroBrasileiro(valorAmericano: number) {
    const valorString = valorAmericano.toFixed(2).toString();
    const partes = valorString.split('.');
    const parteInteira = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return parteInteira + ',' + partes[1];
  }


  function getValores(desconto?: string, isAP?: boolean, totalDivida?: string) {
    if (parcelasNeg.length > 0) {
      const juros = parcelasNeg.map(p => converterParaNumeroAmericano(p.juros)).reduce((prev, next) => prev + next)
      const multa = parcelasNeg.map(p => converterParaNumeroAmericano(p.multa)).reduce((prev, next) => prev + next)
      const valorOriginal = parcelasNeg.map(p => converterParaNumeroAmericano(p.valorprincipal)).reduce((prev, next) => prev + next)
      const valorCorrigido = juros + multa + valorOriginal
      const descontoCal = converterParaNumeroAmericano(desconto || '0,00')
      const total = valorCorrigido - descontoCal

      const jurosGazin = converterParaNumeroAmericano(totalDivida || '0,00') - valorOriginal

      setValoresSimulacao({
        juros: isAP ? converterParaNumeroBrasileiro(jurosGazin) : converterParaNumeroBrasileiro(juros),
        multa: converterParaNumeroBrasileiro(multa),
        valorOriginal: converterParaNumeroBrasileiro(valorOriginal),
        valorCorrigido: isAP ? totalDivida : converterParaNumeroBrasileiro(valorCorrigido),
        desconto: converterParaNumeroBrasileiro(descontoCal),
        total: converterParaNumeroBrasileiro(total)
      })
    } else {
      setValoresSimulacao({
        juros: '0,00',
        multa: '0,00',
        valorOriginal: '0,00',
        valorCorrigido: '0,00',
        desconto: '0,00',
        total: '0,00'
      })
    }

  }

  function addContrato(contrato: Contrato, isOperador?: boolean) {
    if (contratoNeg.numcontrato && !isOperador) {
      setContratoNeg({} as Contrato)
      return
    }
    setContratoNeg(contrato)
  }

  function addParcelasNegGroup(parcelas: Divida[], unSelectAll?: boolean) {
    if (unSelectAll) {
      setParcelasNeg([] as Divida[])
      return
    }
    setParcelasNeg(parcelas)
  }

  function clearParcelasNeg() {
    setParcelasNeg([] as Divida[])
    setSimulacaoAP({} as ConsultaParametrosData)
    setSimulacaoAV({} as ConsultaParametrosData)
    setValoresSimulacao({
      juros: '0,00',
      multa: '0,00',
      valorOriginal: '0,00',
      valorCorrigido: '0,00',
      desconto: '0,00',
      total: '0,00'
    })
    setSimulacaoAPJP({} as ConsultaParametrosArray)
    setSimulacaoCalculadora({} as SimularAcordoData)
    setContratoNeg({} as Contrato)
    setAcordoResponse({} as ConsultaAcordoResponse)
  }

  function setSelection(state: string) {
    setStateSelection(state)
  }


  async function getAcordos(data: ConsultaAcordoRequest) {
    const { data: acordoData } = await acordosMut.mutateAsync(data)
    let acordo = acordoData?.message

    setSelectedAcordoResponse(acordo)
    setAcordoResponse(acordo)
  }

  return (
    <NegContext.Provider value={{
      addParcelasNeg,
      addParcelasNegGroup,
      clearParcelasNeg,
      parcelasNeg,
      setSelection,
      stateSelection,
      addContrato,
      contratoNeg,
      getParamsSimulate,
      simulacaoAP,
      simulacaoAV,
      getValores,
      valoresSimulacao,
      simulacaoAPJP,
      simulacaoJPParcela,
      calcularValores,
      simulcaoCalculadora,
      setDescontos,
      descontos,
      setValorEntrada,
      valorEntrada,
      percentualEntrada,
      setPercentualEntrada,
      setSimulacaoCalculadora,
      getAcordos,
      acordoResponse,
      selectedAcordoResponse,
      setSelectedAcordoResponse,
      parcelaAcordo,
      setParcelaAcordo
    }}>
      {children}
    </NegContext.Provider>
  );
};

export const useNeg = () => useContext(NegContext);